// composables
import { computed, type Ref } from 'vue'
// utilities
import dayjs from 'dayjs'
// stores
import { useTaskStore } from '@/stores/TaskStore'

/**
 * Composable for managing agreement reminders/tasks
 * Handles filtering reminders for the agreement and computing counters
 */
export default function useAgreementReminders(agreementID: Ref<string | number>) {
  // stores
  const taskStore = useTaskStore()

  /**
   * Computed list of reminders filtered for this agreement
   */
  const computedReminders = computed(() => {
    if (!agreementID.value || !taskStore.tasks) {
      return []
    }

    // Convert agreementID to string for reliable comparison
    const agreementIdValue = String(agreementID.value)
    
    return taskStore.tasks.filter(task => {
      // Match reminders linked to this agreement
      // Convert both to strings for reliable comparison
      const taskLinkId = task.linkId ? String(task.linkId) : null
      return task.linkType === 'agreement' && taskLinkId === agreementIdValue && !task.isDeleted
    })
  })

  /**
   * Computed counter for reminders (total and overdue)
   */
  const remindersCounter = computed(() => {
    let countTotal = 0
    let countOverdue = 0

    if (computedReminders.value && computedReminders.value.length > 0) {
      // Remove completed and deleted reminders
      countTotal = computedReminders.value.filter(rem => !rem.isCompleted && !rem.isDeleted).length
      countOverdue = computedReminders.value.filter(
        rem => rem.dueDate && dayjs(rem.dueDate).isBefore(dayjs()) && !rem.isCompleted && !rem.isDeleted
      ).length
    }

    return { total: countTotal, overdue: countOverdue }
  })

  return {
    // computed
    computedReminders,
    remindersCounter
  }
}
